import React, { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Icon,
  Input,
  InputGroup,
  Tooltip,
  VStack,
} from '@chakra-ui/react';
import { InfoIcon } from '@chakra-ui/icons';
import {
  DropdownField,
  FloatingInputField,
  RequiredStyledProvider,
} from '@payler/ui-components';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import * as yup from 'yup';
import { TFunction } from 'i18next';
import { Controller, useFormContext } from 'react-hook-form';
import { TextStyles } from '@payler/ui-theme';
import { ApiErrorText } from '../../components/ApiErrorText/ApiErrorText';
import { useGetAxiosError } from '../../hooks/use-get-axios-error';
import { useUserPermissions } from '../../hooks/user/users';
import {
  useCreateFasterPayoutMutation,
  useDropMcFasterPayoutsQueryCache,
  useFasterPayoutBanksQuery,
  useCanMakeFasterPayout,
} from '../../hooks/faster-payouts/mutations';
import { useMerchantCabinetConfig } from '../../config/ConfigProvider';
import { getBankLogoIcon } from '../../components/BankNameWithLogo/getBankLogoIcon';
import { PaymentPasswordField } from '../../components/PaymentPasswordField/PaymentPasswordField';

type FormData = {
  memberId: string | null;
  phone: string;
  amount: number | null;
  purpose?: string;
  payoutUserData?: string;
  paymentPassword: string;
};

const defaultValues: FormData = {
  memberId: null,
  phone: '',
  amount: null,
  purpose: '',
  payoutUserData: '',
  paymentPassword: '',
};

const formatPhoneForApi = (phone: string) => `7${phone.replace(/\D/g, '')}`;

function createResolver(t: TFunction) {
  const ns = { ns: 'makeFasterPayout' as const };
  return yupResolver(
    yup.object({
      memberId: yup
        .string()
        .nullable()
        .required(t('form.invalidBank', ns)),
      phone: yup
        .string()
        .required(t('form.invalidPhone', ns))
        .test('phone-digits', t('form.invalidPhone', ns), (value) => {
          const digits = (value ?? '').replace(/\D/g, '');
          return digits.length === 10;
        }),
      amount: yup
        .number()
        .typeError(t('form.invalidAmount', ns))
        .required(t('form.invalidAmount', ns))
        .positive(t('form.invalidAmount', ns)),
      paymentPassword: yup
        .string()
        .required(t('form.invalidPassword', ns)),
      purpose: yup.string().nullable(),
      payoutUserData: yup
        .string()
        .nullable()
        .max(1000, t('form.userDataMaxLength', ns)),
    }),
  );
}

type Props = {
  onSubmit?: VoidFunction;
};

const FormComp = ({ onSubmit: onSubmitOuter }: Props) => {
  const [error, setError] = useState('');
  const [bankSearch, setBankSearch] = useState('');
  const [debouncedBankSearch, setDebouncedBankSearch] = useState('');
  const { t } = useTranslation(['makeFasterPayout']);
  const { t: tWithoutNs } = useTranslation();
  const {
    data: banks,
    isLoading: isBanksLoading,
  } = useFasterPayoutBanksQuery(debouncedBankSearch || undefined);
  const createPayout = useCreateFasterPayoutMutation();
  const refreshPayouts = useDropMcFasterPayoutsQueryCache();
  const getError = useGetAxiosError();
  const { canMakeSbpPayout } = useUserPermissions();
  const canMakeFasterPayout = useCanMakeFasterPayout();
  const { apiServer } = useMerchantCabinetConfig();
  const methods = useForm<FormData>({
    defaultValues,
    resolver: createResolver(tWithoutNs),
  });
  const {
    reset,
    formState: { isSubmitSuccessful },
  } = methods;

  useEffect(() => {
    if (isSubmitSuccessful) {
      reset(defaultValues);
      setBankSearch('');
      setDebouncedBankSearch('');
    }
  }, [isSubmitSuccessful, reset]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedBankSearch(bankSearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [bankSearch]);

  const bankOptions = useMemo(
    () =>
      (banks ?? []).map((bank) => ({
        value: bank.memberId ?? String(bank.id),
        label: bank.name,
        icon: getBankLogoIcon(bank.logo, apiServer),
      })),
    [banks, apiServer],
  );

  const handleBankSearchChange = useCallback((value: string) => {
    setBankSearch(value);
  }, []);

  const onSubmit = methods.handleSubmit(async (values) => {
    setError('');
    const { payoutUserData, purpose, memberId, phone, amount, paymentPassword } =
      values;
    if (!memberId || amount == null) return;

    await createPayout.mutateAsync(
      {
        memberId,
        phone: formatPhoneForApi(phone),
        amount,
        currency: 'RUB',
        password: paymentPassword,
        ...(purpose?.trim() ? { purpose: purpose.trim() } : {}),
        ...(payoutUserData?.trim() ? { userData: payoutUserData.trim() } : {}),
      },
      {
        onError: (e: unknown) => {
          setError(getError(e));
          throw e;
        },
        onSuccess: () => {
          refreshPayouts();
          onSubmitOuter?.();
        },
      },
    );
  });

  const canSubmit = canMakeFasterPayout;

  return (
    <FormProvider {...methods}>
      <form onSubmit={onSubmit} autoComplete="off">
        <Box
          position="absolute"
          opacity={0}
          height={0}
          width={0}
          overflow="hidden"
          pointerEvents="none"
          tabIndex={-1}
          aria-hidden
        >
          <input type="text" name="username" autoComplete="username" tabIndex={-1} />
          <input
            type="password"
            name="current-password"
            autoComplete="current-password"
            tabIndex={-1}
          />
        </Box>
        <VStack alignItems="stretch" spacing={2}>
          <DropdownField
            fieldName="memberId"
            label={t('makeFasterPayout:form.bank')}
            floating
            options={bankOptions}
            isSearchable
            onSearchTextChange={handleBankSearchChange}
            isLoading={isBanksLoading && !banks}
            hideClearButton
          />
          <PhoneField />
          <FloatingInputField
            name="amount"
            label={t('makeFasterPayout:form.amount')}
            variant="currency"
            currencySuffix="₽"
            currencyDecimals={2}
            autoComplete="off"
          />
          <PurposeField />
          <UserDataField />
          <PaymentPasswordField
            name="paymentPassword"
            label={t('makeFasterPayout:form.password')}
          />
          {error && <ApiErrorText>{error}</ApiErrorText>}
          <Tooltip
            label={
              !canMakeSbpPayout
                ? t('makeFasterPayout:form.noAccess')
                : !canMakeFasterPayout
                  ? t('makeFasterPayout:form.sbpNotEnabled')
                  : ''
            }
            shouldWrapChildren
          >
            <Button
              type="submit"
              isLoading={createPayout.isPending}
              disabled={!canSubmit}
            >
              {t('makeFasterPayout:form.send')}
            </Button>
          </Tooltip>
        </VStack>
      </form>
    </FormProvider>
  );
};

const PhoneField = () => {
  const { t } = useTranslation(['makeFasterPayout']);
  const { control, getFieldState, formState } = useFormContext<FormData>();
  const errorMessage = getFieldState('phone', formState).error?.message;
  const isError = !!errorMessage && !!formState.touchedFields;

  return (
    <FormControl isInvalid={isError}>
      <RequiredStyledProvider>
        <Box position="relative">
          <InputGroup>
            <Controller
              name="phone"
              control={control}
              render={({ field: { onChange, value, name, ref, onBlur } }) => (
                <Input
                  ref={ref}
                  name={name}
                  variant="floating"
                  type="tel"
                  inputMode="tel"
                  autoComplete="off"
                  pl={5}
                  color="primary.500"
                  value={value ?? ''}
                  onBlur={onBlur}
                  onChange={(event) => {
                    onChange(event.target.value.replace(/\D/g, '').slice(0, 10));
                  }}
                />
              )}
            />
            <FormLabel
              variant="floating"
              textStyle={TextStyles.Caption12Regular}
              top={1}
              fontSize="12px"
            >
              {t('makeFasterPayout:form.phone')}
            </FormLabel>
          </InputGroup>
          <Box
            position="absolute"
            left={2}
            bottom={1}
            color="primary.350"
            pointerEvents="none"
            textStyle={TextStyles.BodyUI16Regular}
          >
            +7
          </Box>
        </Box>
        {isError && errorMessage && (
          <FormHelperText
            color="red.500"
            mt={1}
            textStyle={TextStyles.Caption12Regular}
            px={1.5}
          >
            {errorMessage}
          </FormHelperText>
        )}
      </RequiredStyledProvider>
    </FormControl>
  );
};

const UserDataField = () => {
  const { t } = useTranslation(['makeFasterPayout']);
  return (
    <Box position="relative">
      <FloatingInputField
        name="payoutUserData"
        label={t('makeFasterPayout:form.userData')}
        maxLength={1000}
        pr="28px"
        autoComplete="off"
      />
      <Box
        position="absolute"
        right="8px"
        top="50%"
        transform="translateY(-50%)"
        zIndex={1}
        pointerEvents="auto"
        mt="-12px"
      >
        <Tooltip
          label={t('makeFasterPayout:form.userDataHelp')}
          placement="top"
          borderRadius={0.5}
          backdropFilter="auto"
          backdropBlur={1}
          color="secondary.500"
          bg="primary.350"
          padding={1}
          shouldWrapChildren
        >
          <Icon as={InfoIcon} w="14px" h="14px" color="primary.400" cursor="help" />
        </Tooltip>
      </Box>
    </Box>
  );
};

const PurposeField = () => {
  const { t } = useTranslation(['makeFasterPayout']);
  return (
    <Box position="relative">
      <FloatingInputField
        name="purpose"
        label={t('makeFasterPayout:form.purpose')}
        pr="28px"
        autoComplete="off"
      />
      <Box
        position="absolute"
        right="8px"
        top="50%"
        transform="translateY(-50%)"
        zIndex={1}
        pointerEvents="auto"
        mt="-12px"
      >
        <Tooltip
          label={t('makeFasterPayout:form.purposeHelp')}
          placement="top"
          borderRadius={0.5}
          backdropFilter="auto"
          backdropBlur={1}
          color="secondary.500"
          bg="primary.350"
          padding={1}
          shouldWrapChildren
        >
          <Icon as={InfoIcon} w="14px" h="14px" color="primary.400" cursor="help" />
        </Tooltip>
      </Box>
    </Box>
  );
};

export const MakeFasterPayoutForm = (props: Props) => (
  <Suspense fallback={null}>
    <FormComp {...props} />
  </Suspense>
);

export default MakeFasterPayoutForm;
